import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Globe, ShieldCheck, Network, Handshake, ArrowRight } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';

const benefits = [
  {
    icon: Network,
    title: 'Global Agent Network',
    description: 'Access to thousands of vetted logistics partners across every major trade lane worldwide.'
  },
  {
    icon: ShieldCheck,
    title: 'Verified & Trusted',
    description: 'Every partnership is backed by strict credit verification and industry-leading compliance standards.'
  },
  {
    icon: Globe,
    title: 'Worldwide Coverage',
    description: 'Seamless door-to-door coverage in 50+ countries through our strategic alliance memberships.'
  },
  {
    icon: Handshake,
    title: 'Collaborative Growth',
    description: 'We build long-term relationships that deliver consistent capacity, better rates, and faster transit times.'
  }
];

export default function Partners() {
  React.useEffect(() => {
    document.title = 'Partners | Axiom Global Freight LLC';
    return () => { document.title = 'Axiom Global Freight LLC | Global Logistics & Freight Solutions'; };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative bg-slate-900 pt-40 pb-24 overflow-hidden" data-testid="partners-hero">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
            <span className="inline-block text-amber-400 font-semibold text-sm uppercase tracking-wider mb-4">
              Our Network
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Trusted <span className="text-amber-400">Partners</span> Worldwide
            </h1>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Our strength lies in the alliances we build. Through world-class partnerships,
              Axiom Global Freight delivers reliable capacity and coverage across the globe.
            </p>
          </div>
        </section>

        {/* Featured Partner */}
        <section className="py-24 bg-white" data-testid="featured-partner-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block text-amber-500 font-semibold text-sm uppercase tracking-wider mb-4">
                  Featured Partner
                </span>
                <span className="block text-base md:text-lg font-bold text-slate-900 mb-2 uppercase tracking-wide">
                  JCtrans Logistics Network
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                  A Proud Member of the World's Leading{' '}
                  <span className="text-amber-500">Logistics Alliance</span>
                </h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Axiom Global Freight LLC is an active member of JCtrans, one of the largest
                  and most respected global logistics networks. This membership connects us
                  with thousands of verified freight forwarders across 170+ countries,
                  ensuring your cargo moves smoothly through every leg of its journey.
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Through JCtrans, every partner we work with is financially vetted and
                  performance-verified — giving you complete peace of mind on international
                  shipments.
                </p>
                <a href="/#contact">
                  <Button
                    size="lg"
                    data-testid="partners-cta-button"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 group"
                  >
                    Work With Us
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
              <div className="flex justify-center">
                <Card className="border-slate-200 shadow-2xl w-full max-w-md">
                  <CardContent className="p-12 flex items-center justify-center bg-slate-50 rounded-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_air-cargo-axiom/artifacts/wtmlfv4j_160312-JC%20Rising-logo.png"
                      alt="JCtrans Logistics Network"
                      data-testid="jctrans-partner-logo"
                      className="w-full object-contain"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Turnstone Freight Services */}
        <section className="py-24 bg-slate-50" data-testid="turnstone-partner-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 flex justify-center">
                <Card className="border-slate-200 shadow-2xl w-full max-w-md">
                  <CardContent className="p-12 flex items-center justify-center bg-white rounded-lg">
                    <img
                      src="https://customer-assets-m6fa6gv7.emergentagent.net/job_aaaaf049-a533-466d-bb2d-65e9120c5b95/artifacts/a7mvd4ds_tfs-logo-2.webp"
                      alt="Turnstone Freight Services"
                      data-testid="turnstone-partner-logo"
                      className="w-full object-contain"
                    />
                  </CardContent>
                </Card>
              </div>
              <div className="order-1 lg:order-2">
                <span className="inline-block text-amber-500 font-semibold text-sm uppercase tracking-wider mb-4">
                  Featured Partner
                </span>
                <span className="block text-base md:text-lg font-bold text-slate-900 mb-2 uppercase tracking-wide">
                  Turnstone Freight Services
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                  Specialized UK Customs Clearance at{' '}
                  <span className="text-amber-500">Felixstowe Port</span>
                </h2>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Turnstone Freight Services are a specialized clearance broker based in the UK,
                  predominately servicing Felixstowe Port. They provide clearances for single
                  consignment FCL, Multi Consignment FCL and LCL Shipments.
                </p>
                <a href="https://turnstone-freight.com/" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    data-testid="turnstone-cta-button"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 group"
                  >
                    Work With Us
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Cargo Motive Freight */}
        <section className="py-24 bg-white" data-testid="cargomotive-partner-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block text-amber-500 font-semibold text-sm uppercase tracking-wider mb-4">
                  Featured Partner
                </span>
                <span className="block text-base md:text-lg font-bold text-slate-900 mb-2 uppercase tracking-wide">
                  Cargo Motive Freight
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                  Freight Forwarding Without the{' '}
                  <span className="text-amber-500">Chasing</span>
                </h2>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Cargo Motive Freight was built on a simple observation: importers spend their
                  days chasing — chasing rates that take three days to arrive, chasing updates
                  on containers that went quiet mid-ocean, chasing invoices that don't match
                  the quote.
                </p>
                <a href="https://cargomotivefreight.com/" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    data-testid="cargomotive-cta-button"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 group"
                  >
                    Work With Us
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
              <div className="flex justify-center">
                <Card className="border-slate-200 shadow-2xl w-full max-w-md">
                  <CardContent className="p-12 flex items-center justify-center bg-white rounded-lg">
                    <img
                      src="https://customer-assets-m6fa6gv7.emergentagent.net/job_aaaaf049-a533-466d-bb2d-65e9120c5b95/artifacts/an5mfw1x_Screenshot%202026-08-24%20at%2011.17.51%E2%80%AFAM.png"
                      alt="Cargo Motive Freight"
                      data-testid="cargomotive-partner-logo"
                      className="w-full object-contain"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-24 bg-slate-50" data-testid="partner-benefits-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block text-amber-500 font-semibold text-sm uppercase tracking-wider mb-4">
                Why It Matters
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                What Our Partnerships Mean for You
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((item, index) => (
                <Card key={index} className="border-slate-200 hover:shadow-xl transition-shadow duration-300 group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-amber-500/20 transition-colors">
                      <item.icon className="w-7 h-7 text-amber-500" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-slate-900" data-testid="partners-bottom-cta">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Ship with a <span className="text-amber-400">Global Network</span>?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Leverage our worldwide partnerships for faster, safer, and more cost-effective freight.
            </p>
            <a href="/#contact">
              <Button
                size="lg"
                data-testid="partners-bottom-cta-button"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300"
              >
                Request a Quote
              </Button>
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
